/**
 * Top-3 podium. Entries arrive in rank order [1,2,3]; the podium shows them as [2,1,3] (1st centered,
 * raised), with a crown on 1st and medal-gradient pedestals. Renders only the places that exist, so a
 * board with 1–2 players never shows empty pedestals (spec §7 "Few players").
 */

import { View, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Svg, Path } from 'react-native-svg';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { gameLeaderboardSheetStyles as styles } from './GameLeaderboardSheet.styles';
import {
  GAME_LB_COLORS,
  PODIUM_MEDAL,
  PODIUM_PEDESTAL_HEIGHT,
  PODIUM_AVATAR_SIZE,
  GAME_LB_TEST_IDS,
} from './GameLeaderboardSheet.constants';
import type { GameLeaderboardEntry } from '../../../src/quiz/helpers/gamesApi';

function PodiumPlace({ entry, place }: { entry: GameLeaderboardEntry; place: 1 | 2 | 3 }) {
  const me = entry.isCurrentUser;
  const size = PODIUM_AVATAR_SIZE[place];
  const ringStyle =
    place === 1 ? styles.podiumRingGold : me ? styles.podiumRingMe : styles.podiumRingNone;

  return (
    <View style={styles.podiumPlace} testID={GAME_LB_TEST_IDS.podiumPlace(place)}>
      {place === 1 ? (
        <Svg width={22} height={18} viewBox="0 0 22 18" fill="none" style={styles.podiumCrown}>
          <Path d="M2 16l-1-11 6 4L11 2l4 7 6-4-1 11z" fill={GAME_LB_COLORS.crown} />
        </Svg>
      ) : null}
      <View style={[styles.podiumAvatarWrap, ringStyle]}>
        <FriendAvatar
          size={size}
          avatarId={entry.avatarId}
          avatarConfig={entry.avatarConfig}
          fallbackUrl={entry.avatar ?? undefined}
          nameForInitials={entry.name}
        />
      </View>
      <Text style={[styles.podiumName, me && styles.podiumNameMe]} numberOfLines={1}>
        {entry.name}
      </Text>
      <Text style={styles.podiumScore}>{entry.score}</Text>
      <LinearGradient
        colors={[PODIUM_MEDAL[place], `${PODIUM_MEDAL[place]}22`]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={[styles.podiumPedestal, { height: PODIUM_PEDESTAL_HEIGHT[place] }]}
      >
        <Text style={styles.podiumPedestalRank}>{entry.rank}</Text>
      </LinearGradient>
    </View>
  );
}

export function GameLeaderboardPodium({ top3 }: { top3: GameLeaderboardEntry[] }) {
  // Display order: 2nd (left), 1st (center), 3rd (right). Key by array index, not rank.
  const display: Array<{ entry: GameLeaderboardEntry | undefined; place: 1 | 2 | 3 }> = [
    { entry: top3[1], place: 2 },
    { entry: top3[0], place: 1 },
    { entry: top3[2], place: 3 },
  ];

  return (
    <View style={styles.podiumRow}>
      {display.map(({ entry, place }) => (
        <View key={place} style={styles.podiumCol}>
          {entry ? <PodiumPlace entry={entry} place={place} /> : null}
        </View>
      ))}
    </View>
  );
}
